/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useCallback, useEffect, useState } from 'react';
import Notiflix from 'notiflix';
import produce from 'immer';
import axios, { AxiosError } from 'axios';

import { ActionsContainer, ModalButtonContainer } from './styles';
import { useContract } from '../../hooks/contract';
import api from '../../services/api/api';
import Modal2 from '../../components/Modal2';
import Button from '../../components/Button';

import { useTheme } from '../../hooks/theme';
import { RowProps } from '../Beneficiaries';
import { downloadFileOfBlob } from '../../utils/geterateFileURL';
import { TableRow } from './models';
import { useAuth } from '../../hooks/auth';
import { helper } from './helper';
import { EVENTS } from './events';

export const useBeneficiaryIncludeController = () => {
  const { contract } = useContract();
  const { theme } = useTheme();
  const [requestId, setRequestIdForEdit] = useState('');
  const { user } = useAuth();

  const [showForm, setShowForm] = useState({
    contractId: '',
    editMode: false,
    newMode: false,
    isOwner: true,
  });
  const [showDependentIncludeButton, setShowDependentIncludeButton] =
    useState(false);
  const [selectedRow, setSelectedRow] = useState<TableRow | null>(null);

  const [dependentParams, setDependentParams] = useState({} as RowProps);

  const [dataTable, setDataTable] = useState([] as TableRow[]);

  const getDataToTable = useCallback(async (_contractId: string) => {
    try {
      Notiflix.Block.circle('.notiflix-table-loading');

      const { data } = await api.get(
        `beneficiary-inclusion/accessing-add-beneficiary-function?contractId=${_contractId}`,
      );
      const { content } = data;
      Notiflix.Block.remove('.notiflix-table-loading');
      setDataTable(content);

      if (content.length === 0) {
        setShowForm({
          contractId: _contractId,
          editMode: false,
          newMode: true,
          isOwner: true,
        });
      }
    } catch (error) {
      setShowForm({
        contractId: _contractId,
        editMode: false,
        newMode: true,
        isOwner: false,
      });
      if (axios.isAxiosError(error) && error?.response?.status === 400) {
        setDataTable([]);
      } else {
        Notiflix.Notify.failure(
          'Ops, não conseguimos buscar os dados do servidor... Tente novamente mais tarde.',
        );
      }
    }
  }, []);

  const handleOpenReport = useCallback(async (data: TableRow) => {
    try {
      Notiflix.Block.circle(`.notiflix-report-${data.requestId}`);
      const { data: dataApi } = await api.get(
        '/report/list-of-company-portal-reports?exhibitionLocation=2',
      );
      const { content } = dataApi;
      const { nrSeqRelatorio, Parametros: P } = content[0];

      const { data: dataReport } = await api.post(
        `/report/generate-report`,
        {
          nrSeqRelatorio: nrSeqRelatorio ?? '',
          Parametros: [
            {
              id: P[0].cdParametro,
              valor: data.requestId,
            },
          ],
        },
        {
          responseType: 'blob',
        },
      );
      downloadFileOfBlob(dataReport);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.message) {
        Notiflix.Notify.failure(err.response.data.message);
      } else {
        Notiflix.Notify.failure(
          'Ops... Não conseguimos buscar o relatório no servidor. Por favor, tente novamente mais tarde.',
        );
      }
    } finally {
      Notiflix.Block.remove(`.notiflix-report-${data.requestId}`);
    }
  }, []);

  const handleOpenDetailAndEdit = useCallback(
    (data) => {
      setRequestIdForEdit(data.requestId);
      setDependentParams((prev) => ({
        ...prev,
        contractId: contract.contractId,
        beneficiaryName: data.holderName,
        requestId: data.requestId,
        titularName: data.holderName,
      }));
      setShowForm({
        contractId: contract.contractId,
        editMode: true,
        newMode: false,
        isOwner: data.inclusionType === 1 || data.holderName === null,
      });
    },
    [contract],
  );

  const handleRelease = useCallback(async (row: TableRow) => {
    try {
      Notiflix.Block.circle(`.notiflix-release-${row.requestId}`);
      const { data } = await api.put(`company/release-dependent-registered`, {
        requestId: row.requestId,
      });
      const { message } = data;
      Modal2.Success({
        closable: true,
        autoWidth: true,
        title: 'Perfeito!',
        text: message,
        children: (
          <Button modal onClick={() => Modal2.Close()}>
            Ok
          </Button>
        ),
      });
      setDataTable((prev) =>
        produce(prev, (draft) => {
          const found = draft.find((i) => i.requestId === row.requestId);
          if (found) {
            found.status = 'Aguardando análise Operadora';
          }
        }),
      );
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.message) {
        Modal2.Failure({
          closable: true,
          autoWidth: true,
          title: 'Ooops...',
          text: err.response.data.message,
          children: (
            <Button modal onClick={() => Modal2.Close()}>
              Ok
            </Button>
          ),
        });
      } else {
        Notiflix.Notify.failure(
          'Ops... Não conseguimos enviar sua solicitação para o servidor. Por favor, tente novamente mais tarde.',
        );
      }
    } finally {
      Notiflix.Block.remove(`.notiflix-release-${row.requestId}`);
    }
  }, []);

  const handleOpenInconsistency = useCallback(async (row) => {
    try {
      Notiflix.Block.circle('notiflix-button');
      const { data } = await api.get(
        `/company/get-inconsistency-inclusion?requestNumber=${row.requestId}`,
      );

      const { data: realData } = data;
      const text = realData.map((e: any) => {
        return <p>{e.inconsistencyDescription}</p>;
      });

      if (!data) {
        return;
      }

      Modal2.Generic({
        closable: true,
        title: 'Inconsistências',
        autoWidth: true,
        text,
      });
    } catch (error) {
      const err = error as AxiosError<{
        message: string;
      }>;

      if (err.response?.data?.message) {
        Notiflix.Notify.info(err.response.data.message);
      } else {
        Notiflix.Notify.failure(
          'Ops... Não conseguimos buscar o relatório no servidor. Por favor, tente novamente mais tarde.',
        );
      }
    } finally {
      Notiflix.Block.remove('notiflix-button');
    }
  }, []);

  const [rowExpanded, setRowExpanded] = useState([] as TableRow[]);
  const [beneficiary, setBeneficiary] = useState<RowProps[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);

  const handleIncludeDependent = useCallback(
    async (rowData: TableRow) => {
      if (!beneficiary.length) {
        Notiflix.Notify.failure(
          'É necessário selecionar um beneficiário válido para incluir dependentes.',
        );

        return;
      }

      setStep(2);
    },
    [beneficiary],
  );

  const templateRowExpanded = useCallback(
    (rowData: TableRow) => {
      return (
        <ActionsContainer className="spaceBetween">
          <Button
            sausageLook
            whiteButton
            autoWidth
            onClick={() => handleOpenReport(rowData)}
            className={`notiflix-report-${rowData.requestId} marginRight`}
          >
            Relatório
          </Button>
          {rowData.status === 'Aguardando liberação' && (
            <Button
              sausageLook
              whiteButton
              autoWidth
              onClick={() => handleRelease(rowData)}
            >
              Liberar
            </Button>
          )}
          {rowData.status === 'Solicitação com inconsistência' && (
            <Button
              sausageLook
              whiteButton
              autoWidth
              onClick={() => handleOpenInconsistency(rowData)}
            >
              Inconsistências
            </Button>
          )}
          {rowData.allowEdit === 'S' && (
            <Button
              sausageLook
              whiteButton
              autoWidth
              onClick={() => handleOpenDetailAndEdit(rowData)}
              className={`notiflix-reject-${rowData.requestId}`}
            >
              Editar
            </Button>
          )}

          {rowData.allowAddDependent === 'S' && rowData.disapproved !== 'S' && (
            <Button
              sausageLook
              whiteButton
              autoWidth
              onClick={() => handleIncludeDependent(rowData)}
              className={`notiflix-dependent-include-${rowData.requestId}`}
              // disabled={
              //   !beneficiary.length || isLoading
              //   // rowData.allowAddDependent === 'N'
              // }
            >
              Incluir Dependentes
            </Button>
          )}
        </ActionsContainer>
      );
    },
    [
      handleOpenDetailAndEdit,
      handleOpenReport,
      handleRelease,
      handleOpenInconsistency,
      handleIncludeDependent,
    ],
  );

  const handleSubmitEditRequest = useCallback(
    async (data, files) => {
      Notiflix.Block.circle('.notiflix-save-beneficiary');
      try {
        const cityTitle = data.city;
        const NOT_MAPPED = '';
        const params = {
          voterTitleNumber: NOT_MAPPED,
          adoptionDate: NOT_MAPPED,
          brazilianstatesWhichIssuedCtps: NOT_MAPPED,
          weddingDate: data.weddingDate,
          shortName: NOT_MAPPED,
          marriageCertificateNumber: NOT_MAPPED,
          numberPisPasep: NOT_MAPPED,
          portabilityProtocolNumber: NOT_MAPPED,
          numberCtps: NOT_MAPPED,
          numberSeriesCtps: NOT_MAPPED,
          requestId,

          requestHolderId: data?.requestHolderId,
          nameRequestHolder: data?.nameRequestHolder,
          previousRequestId: data?.previousRequestId,

          contractId: contract.contractId,
          beneficiaryName: data.beneficiaryName,
          email: data.email,
          birthDate: data.birthDate,
          hiringDate: data.hiringDate,
          ddiPhone: data.DDIPhone,
          dddPhone: data.phone?.slice(0, 2),
          phone: data.phone?.slice(2),
          birthCityCode: data.cityOfBirth,
          initialsBirthState: data.stateOfBirth,
          cpf: data.cpf,
          rg: data.rg,
          issuingBody: data.rgAgencyIssuer,
          brazilianstatesWhichIssuedRg: data.rgIssuerState,
          emissionDate: data.rgIssuerDate,
          issuingAgencyCountry: data.rgIssuerCountry,
          maritalStatus: data.maritalStatus,
          physicalSex: data.phisicalSex,
          motherName: data.motherName,
          fatherName: data.fatherName,
          motiveIncludeId: data.includeReason,
          initialsState: data.state,
          birthCertificateCodeAlive: data.declarationOfLive,
          neighborhood: data.neighborhood,
          ddiNumberCellphone: data.DDICellphone,
          dddCellPhone: data.cellphone?.slice(0, 2),
          cellphone: data.cellphone?.slice(2),
          zipCode: data.zipCode,
          nationalityCode: data.nationality,
          address: data.address,
          complement: data.complement,
          addressNumber: data.addressNumber,
          nationalSusCardNumber: data.susCard,
          socialName: data.socialName,
          admissionDate: data.holderHiringDate,
          addressTypePublicPlace: data.placeType,
          ibgeCityCode: cityTitle,
          planId: data.product,
          observation: data.observation,
          descriptionCountyBirthForeign: data.foreignBirthCity,
          foreignRgId: data.rgForeign,
          registrationCode: data.registrationCode,
          datePlanAgreementOrigin: data.contractDateOfOriginPlan,
          sourcePlanPriceValue: data.priceOfOriginPlan,
          ansGuideProtocolNumber: data.ANSGuideProtocolNumber,
          protocolIssueDate: data.issueProtocolDate,
          descriptionOperatorOrigin: data.originOperator,
          codeCoveragePlanOrigin: data.coverageGeographicalOriginalPlan,
          sourcePlanRegistrationCode: data.registerOfOriginalProduct,
          typePlanContractOrigin: data.typeOfContract,
          // relationshipCode: data.relationship,
        };
        const formData = new FormData();
        Object.entries(params).forEach((ar) => {
          formData.append(ar[0], ar[1]);
        });

        Object.keys(files).forEach((key, index) => {
          if (files[key]) {
            formData.append(
              `changeFile[${index}]`,
              files[key].file,
              files[key].name,
            );
          }
        });
        const { data: dataApi } = await api.post(
          `/beneficiary-inclusion/save-details-changes`,
          formData,
        );
        Notiflix.Block.remove('.notiflix-save-beneficiary');
        const { message } = dataApi;
        Notiflix.Notify.success(message);
        return requestId;
      } catch (err) {
        Notiflix.Block.remove('.notiflix-save-beneficiary');
        if (axios.isAxiosError(err) && err.response?.data?.message) {
          Modal2.Failure({
            closable: true,
            autoWidth: true,
            title: 'Ooops...',
            text: err.response.data.message,
            children: (
              <Button modal onClick={() => Modal2.Close()}>
                Ok
              </Button>
            ),
          });
        } else {
          Notiflix.Notify.failure(
            'Ops... Não conseguimos salvar sua requisição no servidor. Por favor, tente novamente mais tarde.',
          );
        }
        return null;
      }
    },
    [contract, requestId],
  );

  const goBackToShowTable = useCallback(() => {
    setShowForm({
      contractId: '',
      editMode: false,
      newMode: false,
      isOwner: false,
    });
    setShowDependentIncludeButton(false);
    setRequestIdForEdit('');
    getDataToTable(contract.contractId);
  }, [contract, getDataToTable]);

  const handleSubmitNewRequest = useCallback(
    async (data, files) => {
      const NOT_MAPPED = '';
      try {
        const cityTitle = data.city;
        Notiflix.Block.circle('.notiflix-save-beneficiary');
        const params = {
          voterTitleNumber: NOT_MAPPED,
          adoptionDate: NOT_MAPPED,
          brazilianstatesWhichIssuedCtps: NOT_MAPPED,
          weddingDate: data.weddingDate,
          shortName: NOT_MAPPED,
          marriageCertificateNumber: NOT_MAPPED,
          numberPisPasep: NOT_MAPPED,
          portabilityProtocolNumber: NOT_MAPPED,
          numberCtps: NOT_MAPPED,
          numberSeriesCtps: NOT_MAPPED,

          contractId: contract.contractId,
          beneficiaryName: data.beneficiaryName,
          email: data.email,
          birthDate: data.birthDate,
          hiringDate: data.hiringDate,
          ddiPhone: data.DDIPhone,
          dddPhone: data.phone?.slice(0, 2),
          phone: data.phone?.slice(2),
          birthCityCode: data.cityOfBirth,
          initialsBirthState: data.stateOfBirth,
          cpf: data.cpf,
          rg: data.rg,
          issuingBody: data.rgAgencyIssuer,
          brazilianstatesWhichIssuedRg: data.rgIssuerState,
          emissionDate: data.rgIssuerDate,
          issuingAgencyCountry: data.rgIssuerCountry,
          maritalStatus: data.maritalStatus,
          physicalSex: data.phisicalSex,
          motherName: data.motherName,
          fatherName: data.fatherName,
          motiveIncludeId: data.includeReason,
          initialsState: data.state,
          birthCertificateCodeAlive: data.declarationOfLive,
          neighborhood: data.neighborhood,
          ddiNumberCellphone: data.DDICellphone,
          dddCellPhone: data.cellphone?.slice(0, 2),
          cellphone: data.cellphone?.slice(2),
          zipCode: data.zipCode,
          nationalityCode: data.nationality,
          address: data.address,
          complement: data.complement,
          addressNumber: data.addressNumber,
          nationalSusCardNumber: data.susCard,
          socialName: data.socialName,
          admissionDate: data.holderHiringDate,
          addressTypePublicPlace: data.placeType,
          ibgeCityCode: cityTitle,
          planId: data.product,
          observation: data.observation,
          descriptionCountyBirthForeign: data.foreignBirthCity,
          foreignRgId: data.rgForeign,
          registrationCode: data.registrationCode,
          datePlanAgreementOrigin: data.contractDateOfOriginPlan,
          sourcePlanPriceValue: data.priceOfOriginPlan,
          ansGuideProtocolNumber: data.ANSGuideProtocolNumber,
          protocolIssueDate: data.issueProtocolDate,
          descriptionOperatorOrigin: data.originOperator,
          codeCoveragePlanOrigin: data.coverageGeographicalOriginalPlan,
          sourcePlanRegistrationCode: data.registerOfOriginalProduct,
          typePlanContractOrigin: data.typeOfContract,
          // relationshipCode: data.relationship,
        };
        const formData = new FormData();
        Object.entries(params).forEach((ar) => {
          formData.append(ar[0], ar[1]);
        });
        Object.keys(files).forEach((key, index) => {
          if (files[key]) {
            formData.append(
              `beneficiaryFile[${index}]`,
              files[key].file,
              files[key].name,
            );
          }
        });

        const { data: dataApi } = await api.post(
          `/beneficiary-inclusion/include-beneficiary`,
          formData,
        );
        Notiflix.Block.remove('.notiflix-save-beneficiary');
        const { message, data: data2 } = dataApi;
        // Notiflix.Notify.success(message);

        setRequestIdForEdit(data2[0].requestId);
        setDependentParams((prev) => ({
          ...prev,
          contractId: contract.contractId,
          userUnimedId: data2[0].requestId,
          webUsername: data2[0].userNumber,
        }));
        setShowDependentIncludeButton(true);

        Modal2.Success({
          closable: false,
          autoWidth: true,
          title: 'Perfeito!',
          subtitle: message,
          text: 'Deseja adicionar um dependente?',
          children: (
            <ModalButtonContainer>
              <Button
                modal
                outlined
                onClick={() => {
                  Modal2.Close();
                  goBackToShowTable();
                }}
              >
                Sair
              </Button>
              <Button
                modal
                onClick={() => {
                  Modal2.Close();
                  window.dispatchEvent(
                    new Event(EVENTS.DISPATCH_DEPENDENT_INCLUDE_BUTTON),
                  );
                }}
              >
                Sim
              </Button>
            </ModalButtonContainer>
          ),
        });

        return data2[0].requestId;
      } catch (err) {
        Notiflix.Block.remove('.notiflix-save-beneficiary');
        if (axios.isAxiosError(err) && err.response?.data?.message) {
          Modal2.Failure({
            closable: true,
            autoWidth: true,
            title: 'Ooops...',
            text: err.response.data.message,
            children: (
              <Button modal onClick={() => Modal2.Close()}>
                Ok
              </Button>
            ),
          });
        } else {
          Notiflix.Notify.failure(
            'Ops... Não conseguimos salvar sua requisição no servidor. Por favor, tente novamente mais tarde.',
          );
        }
        return null;
      }
    },
    [contract, goBackToShowTable],
  );

  useEffect(() => {
    const controller = new AbortController();

    const getBeneficiaryData = async () => {
      if (!selectedRow?.requestId) return;
      const loadingAction = `.notiflix-dependent-include-${selectedRow.requestId}`;

      try {
        setIsLoading(true);
        Notiflix.Block.circle(loadingAction);
        setBeneficiary([
          {
            contractId: user.contractId,
            webUsername: user.webUsername,
            userUnimedId: selectedRow.userUnimedId,
            beneficiaryName: selectedRow.beneficiaryName,
            productId: selectedRow.productId ? selectedRow.productId : null,
            productName: selectedRow.productName,
            requestHolderId: selectedRow.requestId,
          } as RowProps,
        ]);
      } catch (error) {
        const err = error as AxiosError<{ message: string }>;
        console.log(err?.response?.data?.message);
        setBeneficiary([]);
      } finally {
        setIsLoading(false);
        Notiflix.Block.remove(loadingAction);
      }
    };

    if (selectedRow) {
      getBeneficiaryData();
    }

    return () => {
      controller.abort();
    };
  }, [selectedRow, user.contractId, user.webUsername]);

  /**
   * @description Get all contracts and products and data to table
   */
  useEffect(() => {
    if (contract.contractId) {
      getDataToTable(contract.contractId);
    }
  }, [contract.contractId, getDataToTable]);

  return {
    dataTable,
    dependentParams,
    getDataToTable,
    handleSubmitEditRequest,
    handleSubmitNewRequest,
    rowExpanded,
    setRowExpanded,
    setShowDependentIncludeButton,
    setShowForm,
    showDependentIncludeButton,
    showForm,
    templateRowExpanded,
    theme,
    contract,
    requestId,
    setRequestIdForEdit,
    step,
    setStep,
    beneficiary,
    isLoading,
    setBeneficiary,
    setSelectedRow,
    goBackToShowTable,
  };
};
